// Utility functions for the chatbot
import { useState, useEffect } from 'react';

// Common FAQ responses
export const faqResponses = {
  greetings: [
    "Hello! How can I help you today?",
    "Hi there! Welcome to Soranova Technologies. How may I assist you?",
    "Good day! What can I help you with?"
  ],
  services: [
    "At Soranova Technologies, we offer a range of IT services including:",
    "• Web Development - Custom websites, e-commerce platforms, and web applications",
    "• Mobile App Development - Native and cross-platform apps for iOS and Android",
    "• Cloud Solutions - Migration, management, and optimization",
    "• AI Integration - Machine learning and AI solutions for business",
    "• IT Consulting - Strategic technology planning and digital transformation",
    "Would you like to know more about any specific service?"
  ],
  pricing: [
    "Our pricing varies based on project requirements and scope. Here's a general overview:",
    "• Web Development: Starting from $5,000",
    "• Mobile Apps: Starting from $8,000",
    "• Cloud Solutions: Starting from $3,000",
    "• Monthly Maintenance: Starting from $500/month",
    "For a custom quote, please share details about your project, and we'd be happy to provide a detailed estimate.",
    "Would you like to speak with a sales representative?"
  ],
  contact: [
    "You can reach our team through multiple channels:",
    "• Email: info@soranova.com",
    "• Phone: (555) 123-4567",
    "• Address: 123 Tech Street, San Francisco, CA 94107",
    "• Office Hours: Monday to Friday, 9 AM to 6 PM EST",
    "Would you like me to direct you to our contact page for more options?"
  ],
  support: [
    "For technical support, you have several options:",
    "• Email: support@soranova.com",
    "• Support Hotline: (555) 987-6543",
    "• Support Portal: support.soranova.com (login required)",
    "• Support Hours: Monday to Friday, 9 AM to 6 PM EST",
    "For urgent issues outside business hours, please use our emergency support line: (555) 789-0123"
  ],
  company: [
    "Soranova Technologies was founded in 2010 with a mission to deliver innovative IT solutions. Here's a quick overview:",
    "• Team: 50+ technology experts across development, design, and consulting",
    "• Locations: Headquarters in San Francisco with offices in New York and London",
    "• Expertise: Specialized in custom software development, digital transformation, and emerging technologies",
    "• Clients: We've served 200+ clients across various industries including finance, healthcare, retail, and education",
    "Would you like to know more about our team or company history?"
  ],
  process: [
    "Our development process follows these key steps:",
    "1. Discovery & Requirements: We carefully analyze your needs and define project scope",
    "2. Design & Prototyping: Creating user-friendly designs and interactive prototypes",
    "3. Development: Building your solution with clean, maintainable code",
    "4. Testing & QA: Rigorous testing to ensure quality and performance",
    "5. Deployment: Smooth launch with minimal disruption",
    "6. Support & Maintenance: Ongoing support and updates",
    "Would you like more details about any particular stage?"
  ],
  default: [
    "I'm not sure I understand. Could you please rephrase your question or select from our common topics like services, pricing, or support?",
    "I don't have information on that specific topic. Would you like to ask about our services, contact information, or speak with a human representative?",
    "I'm sorry, I couldn't understand your request. Can you try asking in a different way or let me know if you'd like to be connected to our support team?"
  ]
};

// Advanced response generator with context awareness
export const useAdvancedResponseGenerator = () => {
  // Track conversation context
  const [context, setContext] = useState({
    lastTopic: null,
    mentionedServices: false,
    mentionedPricing: false,
    mentionedCompany: false,
    requestedHuman: false,
    messageCount: 0
  });

  // Generate a response based on user input and conversation context
  const generateResponse = (input) => {
    const text = input.toLowerCase();
    let response = "";
    let newContext = { ...context };
    
    // Increment message count
    newContext.messageCount = context.messageCount + 1;
    
    // Check for multiple topics in a single message
    const topicsInMessage = [];
    
    if (text.match(/hello|hi|hey|greetings/)) topicsInMessage.push('greetings');
    if (text.match(/service|offer|provide|develop|build|create/)) topicsInMessage.push('services');
    if (text.match(/price|cost|pricing|quote|how much|package/)) topicsInMessage.push('pricing');
    if (text.match(/contact|reach|call|email|phone/)) topicsInMessage.push('contact');
    if (text.match(/support|help|issue|problem|trouble|fix/)) topicsInMessage.push('support');
    if (text.match(/company|about|team|history|founded|soranova/)) topicsInMessage.push('company');
    if (text.match(/process|approach|methodology|steps|how do you|workflow/)) topicsInMessage.push('process');
    if (text.match(/human|agent|representative|real person|speak to someone/)) {
      newContext.requestedHuman = true;
      topicsInMessage.push('human');
    }
    
    // Handle request for human
    if (newContext.requestedHuman) {
      return {
        text: "I'll connect you with a human representative shortly. While you wait, is there anything specific you'd like them to know about your inquiry?",
        context: newContext
      };
    }
    
    // Handle multiple topics
    if (topicsInMessage.length > 1) {
      // Remove greetings if there are other substantive topics
      if (topicsInMessage.includes('greetings') && topicsInMessage.length > 1) {
        topicsInMessage.splice(topicsInMessage.indexOf('greetings'), 1);
      }
      
      response = "I see you're asking about multiple things. Let me address each:";
      
      // Add responses for each topic (limit to prevent extremely long messages)
      const topicsToAddress = topicsInMessage.slice(0, 2);
      topicsToAddress.forEach(topic => {
        if (topic !== 'human') { // Already handled above
          response += "\n\n" + "Regarding " + topic + ":\n";
          if (topic === 'greetings') {
            response += getRandomResponse(faqResponses.greetings);
          } else {
            response += faqResponses[topic][0]; // Use first response for each topic in multi-topic scenario
          }
          
          // Update context
          newContext.lastTopic = topic;
          if (topic === 'services') newContext.mentionedServices = true;
          if (topic === 'pricing') newContext.mentionedPricing = true;
          if (topic === 'company') newContext.mentionedCompany = true;
        }
      });
      
      // Add follow-up based on context
      if (newContext.messageCount > 2 && !newContext.requestedHuman) {
        response += "\n\nWould you like to speak with a human representative for more detailed information?";
      }
      
      return { text: response, context: newContext };
    }
    
    // Single topic or no recognized topic
    let matchedTopic = topicsInMessage[0];
    
    // If no topic matched explicitly
    if (!matchedTopic) {
      // Check for follow-up questions related to the last topic
      if (context.lastTopic && (
          text.includes("more") || 
          text.includes("details") || 
          text.includes("example") || 
          text.includes("specifically") ||
          text.includes("yes") ||
          text.includes("please")
      )) {
        matchedTopic = context.lastTopic;
        
        // For follow-ups, use a more detailed response if available
        if (faqResponses[matchedTopic].length > 1) {
          // Use a response we haven't used yet
          const followUpIndex = Math.min(
            1, 
            faqResponses[matchedTopic].length - 1
          );
          response = faqResponses[matchedTopic][followUpIndex];
          
          // Add relationship-building follow-up
          if (newContext.messageCount > 2) {
            response += "\n\nIs there anything else you'd like to know about our " + matchedTopic + "?";
          }
          
          return { text: response, context: newContext };
        }
      }
      
      // If no topic detected and no meaningful follow-up context, use default
      matchedTopic = 'default';
    }
    
    // Update context based on the matched topic
    newContext.lastTopic = matchedTopic;
    if (matchedTopic === 'services') newContext.mentionedServices = true;
    if (matchedTopic === 'pricing') newContext.mentionedPricing = true;
    if (matchedTopic === 'company') newContext.mentionedCompany = true;
    
    response = getRandomResponse(faqResponses[matchedTopic]);
    
    // Add contextual suggestions based on the conversation flow
    if (matchedTopic === 'services' && !newContext.mentionedPricing && newContext.messageCount > 1) {
      response += "\n\nWould you like to know about our pricing for these services?";
    } else if (matchedTopic === 'pricing' && !newContext.mentionedServices) {
      response += "\n\nWe offer various service packages. Would you like to learn more about what each service includes?";
    } else if ((matchedTopic === 'services' || matchedTopic === 'pricing') && !newContext.mentionedCompany && newContext.messageCount > 2) {
      response += "\n\nBy the way, would you like to know more about our company and our experience in the industry?";
    }
    
    // Suggest human representative after several messages if complex topics were discussed
    if ((newContext.mentionedServices || newContext.mentionedPricing) && 
        newContext.messageCount > 3 && 
        !newContext.requestedHuman) {
      response += "\n\nFor more personalized information, would you prefer to speak with one of our representatives?";
    }
    
    return { text: response, context: newContext };
  };
  
  return { generateResponse, context, setContext };
};

// Function to get a random response from the response array
export const getRandomResponse = (responses) => {
  const randomIndex = Math.floor(Math.random() * responses.length);
  return responses[randomIndex];
};

// Initial messages to greet the user
export const initialMessages = [
  {
    id: 1,
    text: "Hello! I'm Soranova Assistant, your virtual tech support. How can I help you today?",
    sender: 'bot',
    timestamp: new Date()
  },
  {
    id: 2,
    text: "You can ask me about our services, pricing, or tech support. I'm here to assist you!",
    sender: 'bot',
    timestamp: new Date(Date.now() + 100)
  }
];

// Custom hook for delayed typing effect
export const useTypingEffect = (text, speed = 30) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    setDisplayedText('');
    setIsComplete(false);
    
    if (!text) {
      setIsComplete(true);
      return;
    }
    
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(prevText => prevText + text.charAt(i));
        i++;
      } else {
        clearInterval(timer);
        setIsComplete(true);
      }
    }, speed);
    
    return () => clearInterval(timer);
  }, [text, speed]);
  
  return { displayedText, isComplete };
};